import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Switch,
  FormControlLabel,
  InputAdornment,
} from '@mui/material';
import {
  Label as TagIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import * as tagService from '../services/articleTagService';
import { ConfirmDialog } from '../components/common';

const ArticleTagManagement: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();

  // Data
  const [tags, setTags] = useState<tagService.ArticleTag[]>([]);
  const [totalDocs, setTotalDocs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // Search
  const [searchQuery, setSearchQuery] = useState('');

  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTag, setEditingTag] = useState<tagService.ArticleTag | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingTag, setDeletingTag] = useState<tagService.ArticleTag | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Form
  const [formName, setFormName] = useState('');
  const [formIsActive, setFormIsActive] = useState(true);

  // Load tags
  const loadTags = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await tagService.getTags(page + 1, rowsPerPage, searchQuery || undefined);
      setTags(response.docs || []);
      setTotalDocs(response.totalDocs || 0);
    } catch (err: any) {
      setError(err.message || 'خطا در بارگذاری تگ‌ها');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  // ==================== DIALOG HANDLERS ====================

  const handleOpenCreate = () => {
    setEditingTag(null);
    setFormName('');
    setFormIsActive(true);
    setDialogOpen(true);
  };

  const handleOpenEdit = (tag: tagService.ArticleTag) => {
    setEditingTag(tag);
    setFormName(tag.name);
    setFormIsActive(tag.isActive);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTag(null);
  };

  const handleSubmit = async () => {
    if (!formName.trim()) {
      enqueueSnackbar('نام تگ الزامی است', { variant: 'warning' });
      return;
    }

    setSubmitting(true);
    try {
      const data: tagService.TagFormData = {
        name: formName.trim(),
        isActive: formIsActive,
      };

      if (editingTag) {
        await tagService.updateTag(editingTag._id, data);
        enqueueSnackbar('تگ با موفقیت ویرایش شد', { variant: 'success' });
      } else {
        await tagService.createTag(data);
        enqueueSnackbar('تگ با موفقیت ایجاد شد', { variant: 'success' });
      }

      handleCloseDialog();
      loadTags();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در ذخیره تگ', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (tag: tagService.ArticleTag) => {
    setDeletingTag(tag);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingTag) return;
    setSubmitting(true);
    try {
      await tagService.deleteTag(deletingTag._id);
      enqueueSnackbar('تگ با موفقیت حذف شد', { variant: 'success' });
      setDeleteDialogOpen(false);
      setDeletingTag(null);
      loadTags();
    } catch (err: any) {
      enqueueSnackbar(err.message || 'خطا در حذف تگ', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('fa-IR');
  };

  // ==================== RENDER ====================

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          <TagIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          مدیریت تگ‌های مقالات
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button startIcon={<RefreshIcon />} onClick={loadTags} disabled={loading}>
            بروزرسانی
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreate}
          >
            تگ جدید
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Search */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="جستجو تگ..."
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            setPage(0);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
      </Paper>

      {/* Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>نام</TableCell>
                <TableCell>اسلاگ</TableCell>
                <TableCell align="center">تعداد مقالات</TableCell>
                <TableCell align="center">وضعیت</TableCell>
                <TableCell>تاریخ ایجاد</TableCell>
                <TableCell align="center">عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={32} />
                  </TableCell>
                </TableRow>
              ) : tags.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">تگی یافت نشد</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                tags.map((tag) => (
                  <TableRow key={tag._id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {tag.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary" dir="ltr">
                        {tag.slug}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={tag.articleCount?.toLocaleString('fa-IR') || '۰'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={tag.isActive ? 'فعال' : 'غیرفعال'}
                        color={tag.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{formatDate(tag.createdAt)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                        <Tooltip title="ویرایش">
                          <IconButton
                            color="info"
                            size="small"
                            onClick={() => handleOpenEdit(tag)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="حذف">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={() => handleDeleteClick(tag)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={totalDocs}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50]}
          labelRowsPerPage="تعداد در صفحه:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} از ${count}`}
        />
      </Paper>

      {/* ==================== ADD/EDIT DIALOG ==================== */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingTag ? 'ویرایش تگ' : 'ایجاد تگ جدید'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="نام تگ"
            value={formName}
            onChange={(e) => setFormName(e.target.value)}
            required
            sx={{ mt: 1, mb: 2 }}
            autoFocus
            placeholder="مثال: مراقبت پوست"
          />
          <FormControlLabel
            control={
              <Switch
                checked={formIsActive}
                onChange={(e) => setFormIsActive(e.target.checked)}
              />
            }
            label="فعال"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>انصراف</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !formName.trim()}
            startIcon={submitting ? <CircularProgress size={18} /> : undefined}
          >
            {editingTag ? 'ذخیره تغییرات' : 'ایجاد'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ==================== DELETE DIALOG ==================== */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="حذف تگ"
        message={`آیا از حذف تگ «${deletingTag?.name}» اطمینان دارید؟`}
        confirmText="حذف"
        cancelText="انصراف"
        confirmColor="error"
        loading={submitting}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setDeletingTag(null);
        }}
        onConfirm={handleDeleteConfirm}
      >
        {deletingTag && (deletingTag.articleCount || 0) > 0 && (
          <Alert severity="warning">
            این تگ روی {deletingTag.articleCount} مقاله استفاده شده است. با حذف تگ، از مقالات مربوطه نیز حذف خواهد شد.
          </Alert>
        )}
      </ConfirmDialog>
    </Container>
  );
};

export default ArticleTagManagement;
